import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import type { PuckPageStatus } from "@/lib/cms-puck/types";
import { preserveCanvasScroll } from "@/lib/cms-puck/preserve-canvas-scroll";
import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  Archive,
  ChevronDown,
  EyeOff,
  Globe,
  Loader2,
  Save,
} from "lucide-react";

const menuItemClass =
  "cursor-pointer hover:bg-primary hover:text-white focus:bg-primary focus:text-white data-[highlighted]:bg-primary data-[highlighted]:text-white hover:[&_span]:text-white/90 focus:[&_span]:text-white/90 data-[highlighted]:[&_span]:text-white/90";

export function PagePublicationMenu({
  pageReady,
  pageStatus,
  dirty,
  busy,
  isSaving,
  isUnpublishing,
  isArchiving,
  onSaveDraft,
  onPublish,
  onUnpublish,
  onArchive,
}: {
  pageReady: boolean;
  pageStatus?: PuckPageStatus;
  dirty?: boolean;
  busy?: boolean;
  isSaving?: boolean;
  isUnpublishing?: boolean;
  isArchiving?: boolean;
  onSaveDraft: () => void;
  onPublish: () => void;
  onUnpublish: () => void;
  onArchive: () => void;
}) {
  const cmsNew = useCmsNew();
  const pending = isSaving || isUnpublishing || isArchiving;
  const isPublished = pageStatus === "PUBLISHED";

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) preserveCanvasScroll();
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="default"
          size="sm"
          disabled={!pageReady || busy}
          className="gap-1 text-white"
        >
          {pending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : null}
          {pending
            ? cmsNew("cms.new.publication.button.pending", "En cours…")
            : cmsNew("cms.new.publication.button.label", "Publication")}
          {!pending ? <ChevronDown size={14} /> : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuItem
          onClick={onSaveDraft}
          disabled={pending}
          className={menuItemClass}
        >
          <Save size={14} className="mr-2 shrink-0" />
          <span className="flex flex-col gap-0.5">
            <span>
              {cmsNew(
                "cms.new.auto.text.enregistrer_le_brouillon",
                "Enregistrer le brouillon",
              )}
            </span>
            {dirty ? (
              <span className="text-[10px] font-normal text-amber-600">
                {cmsNew(
                  "cms.new.publication.draft.unsavedChanges",
                  "Modifications non enregistrées",
                )}
              </span>
            ) : null}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onPublish}
          disabled={pending}
          className={menuItemClass}
        >
          <Globe size={14} className="mr-2 shrink-0" />
          <span className="flex flex-col gap-0.5">
            <span className="font-medium">
              {cmsNew("cms.new.auto.text.publier", "Publier")}
            </span>
            {dirty ? (
              <span className="text-[10px] font-normal text-slate-500">
                {cmsNew(
                  "cms.new.publication.publish.savesThenPublishes",
                  "Enregistre puis publie",
                )}
              </span>
            ) : null}
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={onUnpublish}
          disabled={!isPublished || pending}
          title={
            isPublished
              ? cmsNew(
                  "cms.new.publication.unpublish.hint",
                  "Retire la page du site public et la repasse en brouillon.",
                )
              : cmsNew(
                  "cms.new.publication.unpublish.disabledTitle",
                  "Seules les pages publiées peuvent être dépubliées",
                )
          }
          className={cn(menuItemClass, !isPublished && "opacity-50")}
        >
          <EyeOff size={14} className="mr-2 shrink-0" />
          <span className="flex flex-col gap-0.5">
            <span>
              {cmsNew("cms.new.publication.unpublish.label", "Dépublier")}
            </span>
            <span className="text-[10px] font-normal text-slate-500">
              {cmsNew(
                "cms.new.publication.unpublish.sublabel",
                "Repasse en brouillon",
              )}
            </span>
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={onArchive}
          disabled={!isPublished || pending}
          title={
            isPublished
              ? cmsNew(
                  "cms.new.publication.archive.hint",
                  "Retire la page du site et la met de côté (archivée).",
                )
              : cmsNew(
                  "cms.new.publication.archive.disabledTitle",
                  "Seules les pages publiées peuvent être archivées",
                )
          }
          className={cn(menuItemClass, !isPublished && "opacity-50")}
        >
          <Archive size={14} className="mr-2 shrink-0" />
          <span className="flex flex-col gap-0.5">
            <span>
              {cmsNew("cms.new.publication.archive.label", "Archiver")}
            </span>
            <span className="text-[10px] font-normal text-slate-500">
              {cmsNew(
                "cms.new.publication.archive.sublabel",
                "Met de côté (hors brouillons)",
              )}
            </span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
